import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { Feature, Polygon } from 'geojson';
import type { Map as MapboxGLMap } from 'mapbox-gl';
import type { MapWrapper } from './map.model';

export enum ShapeType {
  POLYGON = 'POLYGON',
  CIRCLE = 'CIRCLE',
}
export enum DrawingMode {
  OFF = 'OFF',
  DRAWING = 'DRAWING',
  EDITING = 'EDITING',
}

/**
 * Mapbox Draw event interfaces
 * Using the library's event types with our specific feature types
 */
export interface DrawCreateEvent {
  features: DrawableFeature[];
  type: 'draw.create';
  target: MapboxGLMap;
}

export interface DrawUpdateEvent {
  features: DrawableFeature[];
  action: string;
  type: 'draw.update';
  target: MapboxGLMap;
}

export interface DrawDeleteEvent {
  features: DrawableFeature[];
  type: 'draw.delete';
  target: MapboxGLMap;
}

export interface DrawSelectionChangeEvent {
  features: DrawableFeature[];
  type: 'draw.selectionchange';
  target: MapboxGLMap;
}

export interface UseShapeDrawingOptions {
  map?: MapWrapper | null;
  shapeType?: ShapeType | null;
  isDrawingEnabled?: boolean;
  onShapeCreate?: (shape: DrawableFeature) => void;
  onShapeUpdate?: (shape: DrawableFeature) => void;
  onShapeDelete?: () => void;
}

/**
 * Type-safe access to MapboxDraw constructor and static properties
 * Handles the CommonJS/ESM interop where the module might be wrapped with .default
 */
export interface MapboxDrawConstructor {
  new (options?: MapboxDraw.MapboxDrawOptions): MapboxDraw;
  modes: MapboxDraw.Modes;
}

/**
 * Properties for circle features (optional version)
 * Used during circle creation and preview
 */
export interface CircleProperties {
  isCircle?: boolean;
  isPreview?: boolean;
  isCircleCenter?: boolean;
  isControlPoint?: boolean;
  handle?: string;
  center?: [number, number];
  radius?: number;
}

/**
 * Properties for verified circle features (required version)
 * Used for completed circles that are fully defined
 */
export interface CircleFeatureProperties {
  isCircle: true;
  center: [number, number];
  radius: number;
}

/**
 * A circle feature with verified properties
 */
export type CircleFeature = Feature<Polygon, CircleFeatureProperties> & {
  id: string;
};

/**
 * Union type for drawable features (polygon or circle)
 */
export type DrawableFeature =
  | (Feature<Polygon> & { id: string })
  | CircleFeature;

/**
 * Mapbox Draw mouse event interface
 */
export interface MapboxDrawMouseEvent {
  lngLat: {
    lng: number;
    lat: number;
  };
}

/**
 * Mapbox Draw keyboard event interface
 */
export interface MapboxDrawKeyboardEvent {
  keyCode: number;
}

/**
 * Base Mapbox Draw context interface with common methods
 */
export interface MapboxDrawContextBase {
  newFeature: (feature: Feature) => Feature & { id: string };
  addFeature: (feature: Feature) => void;
  deleteFeature: (featureId: string) => void;
  changeMode: (mode: string, options?: Record<string, unknown>) => void;
}
